import { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useCustomerValidation } from '@/components/CustomerValidationProvider';
import { AlertCircle, CheckCircle, HelpCircle, Target, MousePointer, Loader2 } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';

interface EnhancedDynamicFieldValidatorProps {
  data: Record<string, any>;
  onFieldChange: (fieldName: string, value: any) => void;
  disabled?: boolean;
  enhancedPasteHook?: {
    getFieldProps: (fieldName: string, options?: any) => any;
    focusedField: string | null;
    selectedText: string;
    showPasteFeedback: boolean;
    pasteSuccess: boolean;
    pasteToField: (fieldName: string) => void;
  };
  fieldAISuggestions?: Record<string, { value: string; confidence: number; reasoning?: string }>;
  onApplyAISuggestion?: (fieldName: string, value: string) => void;
  searchingFieldName?: string | null;
  isSearching?: boolean;
  fieldConfidence?: Record<string, number>;
  onRejectAISuggestion?: (fieldName: string) => void;
}

export const EnhancedDynamicFieldValidator = ({ 
  data = {}, 
  onFieldChange, 
  disabled = false,
  enhancedPasteHook,
  fieldAISuggestions,
  onApplyAISuggestion,
  searchingFieldName,
  isSearching,
  fieldConfidence,
  onRejectAISuggestion
}: EnhancedDynamicFieldValidatorProps) => {
  const {
    configsByGroup = {},
    validationState = {},
    updateFieldValue,
    validateAllFields
  } = useCustomerValidation() || {};

  const [fieldInteractions, setFieldInteractions] = useState<Record<string, number>>({});

  // Initialize validation when component mounts or data changes
  useEffect(() => {
    if (data && Object.keys(data).length > 0 && validateAllFields) {
      validateAllFields(data);
    }
  }, [data, validateAllFields]);

  const handleFieldChange = (fieldName: string, value: any) => {
    console.log('Field change:', { fieldName, value });
    
    if (onFieldChange) {
      onFieldChange(fieldName, value);
    }
    if (updateFieldValue) {
      updateFieldValue(fieldName, value);
    }
    
    // Track field interactions for UX analytics
    setFieldInteractions(prev => ({
      ...prev,
      [fieldName]: (prev[fieldName] || 0) + 1
    }));
  };

  const renderField = (config: any) => {
    if (!config) return null;
    
    const value = data?.[config.field_name] || config.default_value || '';
    const validation = validationState?.[config.field_name];
    const hasError = validation && !validation.valid;
    const isFocused = enhancedPasteHook?.focusedField === config.field_name;
    const hasSelectedText = Boolean(enhancedPasteHook?.selectedText);
    const interactionCount = fieldInteractions[config.field_name] || 0;
    const fieldConf = fieldConfidence?.[config.field_name];
    const showSpinner = Boolean(isSearching && searchingFieldName === config.field_name);

    // Enhanced field props with accessibility and paste integration
    const fieldProps = {
      id: config.field_name,
      value,
      onChange: (e: any) => handleFieldChange(config.field_name, e.target?.value || e),
      disabled,
      'aria-describedby': `${config.field_name}-help ${config.field_name}-validation`,
      'aria-invalid': hasError ? 'true' : 'false',
      'aria-required': config.is_mandatory ? 'true' : 'false',
      className: `
        transition-all duration-200 ease-in-out
        ${hasError ? 'border-destructive focus:border-destructive' : ''} 
        ${isFocused ? 'ring-2 ring-primary/50 border-primary' : ''}
        ${hasSelectedText && isFocused ? 'ring-primary border-primary shadow-md' : ''}
      `.trim(),
      style: {
        position: 'relative' as const,
        zIndex: isFocused ? 100 : 10, // Ensure focused fields are always on top
      },
      ...enhancedPasteHook?.getFieldProps(config.field_name, {
        displayName: config.display_name || config.field_name,
        priority: config.is_mandatory ? 10 : 5
      })
    };

    let field;
    switch (config.field_type) {
      case 'textarea':
        field = <Textarea {...fieldProps} rows={3} />;
        break;
      case 'select':
        const options = config.validation_rules?.options || [];
        const validOptions = options.filter((option: any) => option?.value && option.value.trim() !== '');
        field = (
          <Select value={value} onValueChange={(val) => handleFieldChange(config.field_name, val)} disabled={disabled}>
            <SelectTrigger 
              className={`
                ${hasError ? 'border-destructive' : ''} 
                ${isFocused ? 'ring-2 ring-primary/50 border-primary' : ''}
              `.trim()}
              style={{ zIndex: isFocused ? 100 : 10 }}
            >
              <SelectValue placeholder={`Select ${config.display_name || 'option'}`} />
            </SelectTrigger>
            <SelectContent style={{ zIndex: 1000 }}>
              {validOptions.map((option: any) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label || option.value}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        );
        break;
      case 'number':
        field = <Input {...fieldProps} type="number" step="0.01" />;
        break;
      case 'email':
        field = <Input {...fieldProps} type="email" />;
        break;
      case 'date':
        field = <Input {...fieldProps} type="date" />;
        break;
      default:
        field = <Input {...fieldProps} type="text" />;
    }

    return (
      <div key={config?.id || config?.field_name} className="space-y-2 relative">
        <div className="flex items-center gap-2">
          <Label htmlFor={config?.field_name} className="flex items-center gap-2">
            {config?.display_name || config?.field_name || 'Unknown Field'}
            {config?.is_mandatory && (
              <Badge variant="destructive" className="text-xs">
                Required
              </Badge>
            )}
            {config?.priority === 'customer' && (
              <Badge variant="secondary" className="text-xs">
                Customer-specific
              </Badge>
            )}
          </Label>
          
          {config?.help_text && (
            <Tooltip>
              <TooltipTrigger>
                <HelpCircle className="h-4 w-4 text-muted-foreground" />
              </TooltipTrigger>
              <TooltipContent>
                <p>{config.help_text}</p>
              </TooltipContent>
            </Tooltip>
          )}
          
          {/* Enhanced validation and focus indicators */}
          <div className="ml-auto flex items-center gap-1">
            {typeof fieldConf === 'number' && (
              <Badge variant={fieldConf >= 90 ? 'default' : fieldConf >= 70 ? 'secondary' : 'outline'} className="h-6 px-2 text-xs">
                {Math.round(fieldConf)}%
              </Badge>
            )}
            {showSpinner && (
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
            )}
            {isFocused && fieldAISuggestions?.[config.field_name] && (
              <>
                <Button
                  size="sm"
                  variant="secondary"
                  className="h-6 px-2 text-xs"
                  onClick={() => onApplyAISuggestion?.(config.field_name, fieldAISuggestions[config.field_name].value)}
                  title={`AI suggests: ${fieldAISuggestions[config.field_name].value}`}
                >
                  Apply “{String(fieldAISuggestions[config.field_name].value).slice(0, 16)}{String(fieldAISuggestions[config.field_name].value).length > 16 ? '…' : ''}” ({Math.round((fieldAISuggestions[config.field_name].confidence || 0) * 100)}%)
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-6 px-2 text-xs"
                  onClick={() => onRejectAISuggestion?.(config.field_name)}
                >
                  Reject
                </Button>
              </>
            )}
            {isFocused && hasSelectedText && (
              <div className="flex items-center gap-1">
                <Target className="h-3 w-3 text-primary animate-pulse" />
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-6 px-2 text-xs"
                  onClick={() => enhancedPasteHook?.pasteToField(config.field_name)}
                >
                  Paste
                </Button>
              </div>
            )}
            {isFocused && !hasSelectedText && !fieldAISuggestions?.[config.field_name] && (
              <div className="flex items-center gap-1 text-xs text-muted-foreground">
                <MousePointer className="h-3 w-3" />
                <span>Select text to paste</span>
              </div>
            )}
            {validation && (
              <div>
                {validation.valid ? (
                  <CheckCircle className="h-4 w-4 text-green-500" />
                ) : (
                  <AlertCircle className="h-4 w-4 text-destructive" />
                )}
              </div>
            )}
          </div>
        </div>
        
        {/* Enhanced field wrapper with focus ring */}
        <div className={`
          relative 
          ${isFocused ? 'ring-1 ring-primary/30 rounded-md p-1 -m-1' : ''}
        `}>
          {field}

          {showSpinner && (
            <Loader2 className="absolute right-2 top-2 h-4 w-4 animate-spin text-muted-foreground" />)
          }
          
          {/* Paste feedback overlay */}
          {enhancedPasteHook?.showPasteFeedback && isFocused && (
            <div className={`
              absolute inset-0 pointer-events-none rounded-md border-2
              ${enhancedPasteHook.pasteSuccess 
                ? 'border-green-500 bg-green-500/10' 
                : 'border-red-500 bg-red-500/10'
              }
              animate-in fade-in duration-200
            `} />
          )}
        </div>
        
        {/* Enhanced validation messages */}
        {(!isSearching && isFocused && typeof fieldConf === 'number' && fieldConf < 70) && (
          <div className="text-xs text-muted-foreground">No confident match found</div>
        )}
        {validation && !validation.valid && validation.errors && (
          <div className="space-y-1">
            {validation.errors.map((error, index) => (
              <Alert key={index} variant="destructive" className="py-2">
                <AlertCircle className="h-3 w-3" />
                <AlertDescription className="text-xs">{error || 'Validation error'}</AlertDescription>
              </Alert>
            ))}
          </div>
        )}
        
        {/* Interaction counter for debugging (only in dev) */}
        {process.env.NODE_ENV === 'development' && interactionCount > 0 && (
          <div className="text-xs text-muted-foreground">
            Interactions: {interactionCount}
          </div>
        )}
      </div>
    );
  };

  if (Object.keys(configsByGroup).length === 0) {
    return (
      <Card>
        <CardContent className="p-8 text-center">
          <AlertCircle className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-medium mb-2">No Validation Rules</h3>
          <p className="text-muted-foreground">
            No field validation rules are configured for this customer.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6" style={{ isolation: 'isolate' }}>
      {/* Enhanced selection status indicator */}
      {enhancedPasteHook?.selectedText && (
        <div className="mb-6 p-4 bg-green-50 dark:bg-green-950 border border-green-200 dark:border-green-800 rounded-lg shadow-sm">
          <div className="flex items-center gap-2 text-green-800 dark:text-green-200">
            <span className="text-green-600 dark:text-green-400 text-lg">✓</span>
            <span className="font-medium">Text Selected & Ready to Paste</span>
          </div>
          <p className="text-green-700 dark:text-green-300 text-sm mt-2 break-words">
            "{enhancedPasteHook.selectedText.slice(0, 120)}{enhancedPasteHook.selectedText.length > 120 ? '...' : ''}"
          </p>
          <div className="flex items-center gap-2 mt-3">
            <p className="text-green-600 dark:text-green-400 text-xs">
              💡 Click on any field below to paste this text automatically
            </p>
            {enhancedPasteHook.focusedField && (
              <span className="px-2 py-1 bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200 rounded text-xs font-medium">
                Target: {enhancedPasteHook.focusedField}
              </span>
            )}
          </div>
        </div>
      )}

      {Object.entries(configsByGroup || {}).map(([group, configs]) => {
        if (!configs || !Array.isArray(configs)) return null;
        
        return (
          <Card key={group} className="relative">
            <CardHeader>
              <CardTitle className="text-base capitalize flex items-center gap-2">
                {group?.replace('_', ' ') || 'Unknown Group'}
                <Badge variant="outline" className="text-xs">
                  {configs.filter(c => c?.is_mandatory).length} required
                </Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-2 gap-4">
              {configs
                .filter(config => config != null)
                .sort((a, b) => (a?.sort_order || 0) - (b?.sort_order || 0))
                .map(renderField)
                .filter(Boolean)}
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
};